import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Avatar,
  Switch,
  Fade,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import SearchBar from '../userManagement/Filter/Searchbar';
import NoData from '../../../components/NoData';
import Loader from '../../../components/AnimatedLoader';
import BranchManagerForm from './BranchManagerForm';
import { theme as colorTheme } from '../../../Convoze.theme';

const primary = colorTheme('palette', 'primary').value;
const neutral = colorTheme('palette', 'neutral').value;
const ROWS_PER_PAGE = 10;

const primaryOutlinedButtonSx = {
  color: primary,
  borderColor: primary,
  '&:hover': {
    borderColor: primary,
    backgroundColor: 'rgba(111, 66, 224, 0.08)'
  }
};

export default function BranchManagerAccountsTab({ onMessage }) {
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(nipponBotApiEndpoints.branchManagerAccountList, { headers: authHeaders });
      setAccounts(response?.data?.data || []);
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to load Branch Manager accounts.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = accounts.filter((account) => {
    if (!search.trim()) return true;
    const term = search.trim().toLowerCase();
    return [account.firstName, account.lastName, account.employeeId]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const start = (page - 1) * ROWS_PER_PAGE;
  const paginated = filtered.slice(start, start + ROWS_PER_PAGE);
  const emptyRows = Math.max(0, ROWS_PER_PAGE - paginated.length);

  const addAccount = () => {
    setEditingAccount(null);
    setShowForm(true);
  };

  const editAccount = (account) => {
    setEditingAccount(account);
    setShowForm(true);
  };

  const saveClose = () => {
    loadAccounts();
    setShowForm(false);
    setEditingAccount(null);
  };

  const handleOpenDelete = (event, id) => {
    event.stopPropagation();
    setSelectedId(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedId(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedId) return;
    try {
      await axiosInstance.delete(`${nipponBotApiEndpoints.branchManagerAccountDelete}/${selectedId}`, { headers: authHeaders });
      onMessage?.('Branch Manager deleted successfully.', 'success');
      await loadAccounts();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to delete Branch Manager.', 'error');
    } finally {
      handleCloseDelete();
    }
  };

  const toggleStatus = async (account, statusToChange) => {
    try {
      await axiosInstance.put(`${nipponBotApiEndpoints.branchManagerAccountUpdate}/${account.id}`, { status: statusToChange }, { headers: authHeaders });
      await loadAccounts();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  const headers = ['Branch Manager', 'Employee ID', 'Created', 'Actions'];

  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          sx={{
            backgroundColor: primary,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            px: { xs: 1, sm: 2.5 },
            py: { xs: 0.75, sm: 1.25 },
            height: { xs: '32px', sm: '36px' },
            '&:hover': { backgroundColor: primary }
          }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addAccount}
        >
          Add Branch Manager
        </Button>
      </Box>

      <SearchBar value={search} onChange={(value) => { setSearch(value); setPage(1); }} />

      {loading ? (
        <Box sx={{ width: '100%', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ width: '100%', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NoData />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', mt: 1 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, py: { xs: 1, sm: 2 }, color: 'text.primary', borderBottom: '1px solid #e0e0e0', fontSize: { xs: 12, sm: 14 }, minWidth: 80 }}>
                      <Typography fontWeight={600} variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>{h}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((account) => {
                  const initials = (account.firstName?.[0] || '?').toUpperCase();
                  return (
                    <Fade in key={account.id}>
                      <TableRow hover sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                        <TableCell sx={{ py: 1 }}>
                          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
                            <Avatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, bgcolor: primary, fontWeight: 700 }}>
                              {initials}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600} fontSize={{ xs: '0.75rem', sm: '0.875rem' }}>
                                {account.firstName} {account.lastName}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography fontSize={{ xs: '0.7rem', sm: '0.75rem' }}>{account.employeeId || '-'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight="500">{moment.utc(account.createdAt).format('MMM DD, YYYY')}</Typography>
                          <Typography variant="body2" color="text.secondary">{moment.utc(account.createdAt).format('hh:mm A')}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IconButton size="small" onClick={(event) => { event.stopPropagation(); editAccount(account); }}>
                              <EditOutlinedIcon style={{ color: neutral }} fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={(event) => handleOpenDelete(event, account.id)} color="error">
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                            <Switch
                              checked={account.status === 'active'}
                              onChange={(event) => { event.stopPropagation(); toggleStatus(account, account.status === 'active' ? 'inactive' : 'active'); }}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase': {
                                  '&.Mui-checked': { color: primary },
                                  '&:not(.Mui-checked)': { color: '#ebebeb96' }
                                },
                                '& .MuiSwitch-track': { backgroundColor: 'gray', opacity: 1 }
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  );
                })}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={99} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(event, value) => setPage(value)}
              sx={{ '& .Mui-selected': { backgroundColor: `${primary} !important`, color: '#fff' } }}
            />
          </Box>

          <Dialog open={openDelete} onClose={handleCloseDelete}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>Are you sure you want to delete this Branch Manager?</DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDelete} variant="outlined" sx={primaryOutlinedButtonSx}>Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" sx={{ bgcolor: primary, color: '#fff', '&:hover': { bgcolor: primary } }}>Delete</Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      <BranchManagerForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditingAccount(null); }}
        onSave={saveClose}
        account={editingAccount}
        accounts={accounts}
      />

      {showForm && (
        <Box
          onClick={() => setShowForm(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 1200,
            display: { xs: 'none', md: 'block' }
          }}
        />
      )}
    </Box>
  );
}
